import React from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { FileDownloadOutlined } from '@mui/icons-material';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { buildCsvTemplate } from '../../utils/csvTemplate';
import { ExampleHint } from './ExampleHint';
import { StatusChip } from './StatusChip';

export interface CsvTemplateColumn {
  name: string;
  /** `true` is required, `'one-of'` means at least one of the columns marked One of, absent is optional. */
  required?: boolean | 'one-of';
  description: string;
  example?: string;
}

export interface CsvTemplateHintProps {
  /** e.g. `sku-overrides-template.csv`. */
  fileName: string;
  /** Header order = template order. */
  columns: CsvTemplateColumn[];
  /** Two rows, each the same length as `columns`. */
  exampleRows: string[][];
  /** Accordion title; defaults to `csvTemplate.title`. */
  title?: string;
  /** One line above the table. */
  intro?: React.ReactNode;
  /** Download button only, no accordion, for a tight toolbar. */
  compact?: boolean;
  /** Accordion only, no button, for a page that already has the download button elsewhere. */
  explainerOnly?: boolean;
  /** Button size; `small` by default, `medium` beside the page's other toolbar buttons. */
  size?: 'small' | 'medium';
  /** Buttons shown beside Download template, e.g. an export of the store's own products. */
  extraActions?: React.ReactNode;
}

/** Saves `text` as `fileName` through a temporary link. */
const saveCsv = (fileName: string, text: string) => {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

/**
 * "Download template" plus a collapsed "Expected columns" explainer for any
 * upload that takes a CSV. The column list is the contract with the server
 * parser, so each caller cites the parser beside its column definitions.
 */
export const CsvTemplateHint: React.FC<CsvTemplateHintProps> = ({
  fileName,
  columns,
  exampleRows,
  title,
  intro,
  compact = false,
  explainerOnly = false,
  size = 'small',
  extraActions,
}) => {
  const { t } = useAppTranslation();

  const download = () => saveCsv(fileName, buildCsvTemplate(columns, exampleRows));

  const button = (
    <Button size={size} variant="outlined" startIcon={<FileDownloadOutlined />} onClick={download}>
      {t('csvTemplate.download')}
    </Button>
  );
  const actions = extraActions ? (
    <Box sx={{ display: 'inline-flex', gap: 1, flexWrap: 'wrap' }}>
      {button}
      {extraActions}
    </Box>
  ) : button;
  if (compact) return actions;

  const requiredChip = (required: CsvTemplateColumn['required']) => {
    if (required === true) return <StatusChip label={t('csvTemplate.required')} tone="problem" />;
    if (required === 'one-of') return <StatusChip label={t('csvTemplate.oneOf')} tone="caution" />;
    return <StatusChip label={t('csvTemplate.optional')} tone="neutral" />;
  };

  const explainer = (
    <ExampleHint title={title ?? t('csvTemplate.title')}>
      {intro && (
        <Typography variant="body2" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
          {intro}
        </Typography>
      )}
      <TableContainer>
        <Table size="small" aria-label={title ?? t('csvTemplate.title')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('csvTemplate.column')}</TableCell>
              <TableCell>{t('csvTemplate.requiredHeading')}</TableCell>
              <TableCell>{t('csvTemplate.description')}</TableCell>
              <TableCell>{t('csvTemplate.example')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {columns.map((column) => (
              <TableRow key={column.name}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography variant="body2" component="code">{column.name}</Typography>
                </TableCell>
                <TableCell>{requiredChip(column.required)}</TableCell>
                <TableCell sx={{ minWidth: 200 }}>{column.description}</TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}>
                  {column.example ? <Typography variant="body2" component="code">{column.example}</Typography> : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </ExampleHint>
  );
  if (explainerOnly) return explainer;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start' }}>
      {actions}
      <Box sx={{ width: '100%' }}>{explainer}</Box>
    </Box>
  );
};

export default CsvTemplateHint;
